import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { resetTourForUser, startTour } from "@/components/OnboardingTour";
import { Compass } from "lucide-react";

const CHANNELS = [
    { key: "email_ticket_created", label: "Confirmación de ticket creado", help: "Recibo un correo cuando creo un ticket nuevo." },
    { key: "email_ticket_updated", label: "Actualizaciones de mis tickets", help: "Me notifican por correo cuando hay respuesta o cambia el estado." },
    { key: "email_weekly_report", label: "Reporte semanal por empresa", help: "Para administradores: resumen semanal los lunes 08:00 CDMX.", roles: ["admin", "superadmin"] },
    { key: "email_weekly_my_scope", label: "Reporte semanal de mi alcance", help: "Para super agentes / super clientes: resumen semanal los lunes 08:15 CDMX.", roles: ["super_agent", "super_customer"] },
    { key: "email_view_alerts", label: "Alertas de vistas guardadas", help: "Correo cuando alguna de mis vistas guardadas crece en la bandeja." },
];

export default function Profile() {
    const { user, refresh } = useAuth();
    const [companies, setCompanies] = useState([]);
    const [name, setName] = useState(user?.name || "");
    const [companyId, setCompanyId] = useState(user?.company_id || "");
    const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
    const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
    const [prefs, setPrefs] = useState(null);
    const [prefsSaving, setPrefsSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [c, p] = await Promise.all([
                    api.get("/companies", { params: { scope: "mine" } }),
                    api.get("/me/notification-prefs"),
                ]);
                setCompanies(c.data);
                setPrefs(p.data);
            } catch (_e) { /* ignore */ }
        })();
    }, []);

    const saveProfile = async (e) => {
        e.preventDefault(); setMsg(""); setErr("");
        try {
            await api.patch(`/users/${user.user_id}`, { name, company_id: companyId || null });
            await refresh();
            setMsg("Perfil actualizado.");
        } catch (error) { setErr(formatApiError(error)); }
    };

    const changePw = async (e) => {
        e.preventDefault(); setMsg(""); setErr("");
        try {
            await api.post("/auth/change-password", pwForm);
            setPwForm({ current_password: "", new_password: "" });
            setMsg("Contraseña actualizada.");
        } catch (error) { setErr(formatApiError(error)); }
    };

    const toggleChannel = async (key) => {
        const next = { ...prefs, [key]: !prefs[key] };
        setPrefs(next);
        setPrefsSaving(true);
        try {
            await api.patch("/me/notification-prefs", { [key]: next[key] });
            toast(next[key] ? "Notificación activada" : "Notificación desactivada");
        } catch (error) {
            toast.error(formatApiError(error));
            setPrefs(prefs); // revert
        } finally {
            setPrefsSaving(false);
        }
    };

    const setTheme = async (theme) => {
        setMsg(""); setErr("");
        try {
            await api.patch(`/users/${user.user_id}`, { theme });
            await refresh();
            toast(theme === "light" ? "Tema claro activado." : "Tema oscuro activado.");
        } catch (error) { setErr(formatApiError(error)); }
    };

    const currentTheme = user?.theme || "dark";

    return (
        <div data-testid="profile-page">
            <div className="page-head">
                <div>
                    <h1>Mi perfil</h1>
                    <p>Tus datos básicos y seguridad de cuenta.</p>
                </div>
            </div>

            {msg && <div className="alert alert-ok" data-testid="profile-ok">{msg}</div>}
            {err && <div className="alert alert-error" data-testid="profile-error">{err}</div>}

            <div className="row row-2">
                <div className="card card-padded">
                    <h3 style={{ margin: "0 0 14px" }}>Datos básicos</h3>
                    <form onSubmit={saveProfile} className="row" style={{ gap: 14 }}>
                        <div className="field">
                            <label>Nombre</label>
                            <input className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
                        </div>
                        <div className="field">
                            <label>Email</label>
                            <input className="input" value={user?.email || ""} disabled />
                        </div>
                        <div className="field">
                            <label>Empresa</label>
                            <select className="select" value={companyId || ""} onChange={(e) => setCompanyId(e.target.value)} data-testid="profile-company">
                                <option value="">— ninguna —</option>
                                {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                            </select>
                        </div>
                        <button className="btn btn-primary" type="submit" data-testid="profile-save">Guardar</button>
                    </form>
                </div>

                <div className="card card-padded">
                    <h3 style={{ margin: "0 0 14px" }}>Cambiar contraseña</h3>
                    {user?.auth_provider === "google" ? (
                        <div className="text-muted text-sm">Tu cuenta usa Google. No aplica cambio de contraseña local.</div>
                    ) : (
                        <form onSubmit={changePw} className="row" style={{ gap: 14 }}>
                            <div className="field">
                                <label>Contraseña actual</label>
                                <input type="password" className="input" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} required data-testid="profile-pw-current" />
                            </div>
                            <div className="field">
                                <label>Nueva contraseña</label>
                                <input type="password" className="input" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} required minLength={8} data-testid="profile-pw-new" />
                            </div>
                            <button className="btn btn-primary" type="submit" data-testid="profile-pw-save">Actualizar contraseña</button>
                        </form>
                    )}
                </div>
            </div>

            <div className="card card-padded mt-4" data-testid="profile-notifications">
                <h3 style={{ margin: "0 0 6px" }}>Preferencias de notificación</h3>
                <p className="text-muted text-sm" style={{ marginTop: 0, marginBottom: 14 }}>
                    Controla qué correos deseas recibir de itotal4u. Los mensajes críticos (bienvenida, restablecimiento de contraseña y avisos del superadministrador) siempre se envían.
                </p>
                {!prefs && <div className="text-muted">Cargando…</div>}
                {prefs && CHANNELS.filter((c) => !c.roles || c.roles.includes(user?.role)).map((c) => {
                    const active = !!prefs[c.key];
                    return (
                        <div key={c.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "10px 0", borderTop: "1px solid var(--border)" }} data-testid={`pref-row-${c.key}`}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600 }}>{c.label}</div>
                                <div className="text-muted text-sm">{c.help}</div>
                            </div>
                            <button
                                onClick={() => toggleChannel(c.key)}
                                disabled={prefsSaving}
                                role="switch"
                                aria-checked={active}
                                data-testid={`pref-toggle-${c.key}`}
                                style={{
                                    position: "relative", flexShrink: 0,
                                    width: 46, height: 24, borderRadius: 999,
                                    background: active ? "var(--accent)" : "var(--bg-2)",
                                    border: "1px solid " + (active ? "var(--accent)" : "var(--border-strong)"),
                                    cursor: "pointer", transition: "background .15s ease",
                                }}
                            >
                                <span style={{
                                    position: "absolute", top: 2, left: active ? 24 : 2,
                                    width: 18, height: 18, borderRadius: "50%",
                                    background: "#fff", transition: "left .15s ease",
                                }} />
                            </button>
                        </div>
                    );
                })}
            </div>

            <div className="card card-padded mt-4" data-testid="profile-theme">
                <h3 style={{ margin: "0 0 6px" }}>Apariencia</h3>
                <p className="text-muted text-sm" style={{ marginTop: 0 }}>Elige el tema con el que prefieras trabajar. Se aplicará a toda la app y a los correos que recibas.</p>
                <div style={{ display: "flex", gap: 12, marginTop: 14 }}>
                    <button
                        type="button"
                        onClick={() => setTheme("dark")}
                        data-testid="theme-option-dark"
                        style={{
                            flex: 1, padding: 14, borderRadius: 10, cursor: "pointer", textAlign: "left",
                            background: "#0F0F10", color: "#F5F5F5",
                            border: `2px solid ${currentTheme === "dark" ? "var(--accent)" : "transparent"}`,
                        }}
                    >
                        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8, opacity: .6 }}>Modo oscuro</div>
                        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Dark · vigente</div>
                        <div style={{ display: "flex", gap: 6 }}>
                            <span style={{ width: 18, height: 18, borderRadius: 999, background: "#DC2626", border: "1px solid #fff2" }} />
                            <span style={{ width: 18, height: 18, borderRadius: 999, background: "#17171A", border: "1px solid #fff2" }} />
                            <span style={{ width: 18, height: 18, borderRadius: 999, background: "#26262B", border: "1px solid #fff2" }} />
                            <span style={{ width: 18, height: 18, borderRadius: 999, background: "#F5F5F5", border: "1px solid #fff2" }} />
                        </div>
                        {currentTheme === "dark" && <div style={{ marginTop: 12, fontSize: 11, color: "#DC2626", fontWeight: 600 }}>✓ ACTIVO</div>}
                    </button>
                    <button
                        type="button"
                        onClick={() => setTheme("light")}
                        data-testid="theme-option-light"
                        style={{
                            flex: 1, padding: 14, borderRadius: 10, cursor: "pointer", textAlign: "left",
                            background: "#F8FAFC", color: "#0F172A",
                            border: `2px solid ${currentTheme === "light" ? "#991B1B" : "transparent"}`,
                            boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 4px 16px -4px rgba(15,23,42,0.08)",
                        }}
                    >
                        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8, color: "#64748B" }}>Modo claro</div>
                        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Light · Snow Slate</div>
                        <div style={{ display: "flex", gap: 6 }}>
                            <span style={{ width: 18, height: 18, borderRadius: 999, background: "#991B1B", border: "1px solid #E5E7EB" }} />
                            <span style={{ width: 18, height: 18, borderRadius: 999, background: "#FFFFFF", border: "1px solid #E5E7EB" }} />
                            <span style={{ width: 18, height: 18, borderRadius: 999, background: "#F1F5F9", border: "1px solid #E5E7EB" }} />
                            <span style={{ width: 18, height: 18, borderRadius: 999, background: "#0F172A", border: "1px solid #E5E7EB" }} />
                        </div>
                        {currentTheme === "light" && <div style={{ marginTop: 12, fontSize: 11, color: "#991B1B", fontWeight: 600 }}>✓ ACTIVO</div>}
                    </button>
                </div>
            </div>

            <div className="card card-padded mt-4" data-testid="profile-metadata">
                <h3 style={{ margin: "0 0 14px" }}>Identidad</h3>
                <div className="row row-2">
                    <div><div className="text-dim text-xs mono">USER ID</div><div className="mono">{user?.user_id}</div></div>
                    <div><div className="text-dim text-xs mono">ROL</div><div><span className={`badge role-${user?.role}`}>{user?.role}</span></div></div>
                    <div><div className="text-dim text-xs mono">PROVEEDOR AUTH</div><div><span className={`badge ${user?.auth_provider === "google" ? "provider-google" : ""}`}>{user?.auth_provider}</span></div></div>
                    <div><div className="text-dim text-xs mono">ALTA</div><div className="mono text-sm">{user?.created_at ? new Date(user.created_at).toLocaleString("es-MX") : "—"}</div></div>
                </div>
            </div>

            <div className="card card-padded mt-4" data-testid="profile-onboarding">
                <h3 style={{ margin: "0 0 6px", display: "flex", alignItems: "center", gap: 8 }}>
                    <Compass size={18} /> Tour guiado
                </h3>
                <p className="text-muted" style={{ margin: "0 0 14px", fontSize: 13 }}>
                    ¿Quieres repasar las secciones de la app? Lanza el tour guiado cuando lo necesites. También puedes iniciarlo desde el botón "Tour" en la barra superior.
                </p>
                <button
                    type="button"
                    className="btn"
                    data-testid="profile-restart-tour"
                    onClick={() => {
                        if (!user) return;
                        resetTourForUser(user.user_id);
                        startTour();
                    }}
                >
                    Iniciar tour ahora
                </button>
            </div>
        </div>
    );
}
